/**
 * NabidkyWorkspace — company settings for price offers: how many days an offer
 * stays valid ("Platnost nabídky je N dnů" on the vozidla + ojeté nabídka
 * printouts). Spravce-gated server-side (`/v1/firma/nabidky`); changes land in
 * Historie.
 */

import { useEffect, useState } from 'react';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Field, Fields } from '@/components/Field';
import { Skeleton } from '@/components/Skeleton';
import { TextInput } from '@/components/TextInput';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { formatDays } from '@/modules/vozidla/nabidka-helpers';
import { useFirmaNabidkyQuery, useUpdateFirmaNabidkyMutation } from './api';

interface NabidkyWorkspaceProps {
  canManage: boolean;
}

const MIN_DNI = 1;
const MAX_DNI = 365;

export function NabidkyWorkspace({ canManage }: NabidkyWorkspaceProps) {
  const query = useFirmaNabidkyQuery(canManage);
  const save = useUpdateFirmaNabidkyMutation();
  const [dni, setDni] = useState('');

  useEffect(() => {
    if (query.data) setDni(String(query.data.platnost_dni));
  }, [query.data]);

  if (!canManage) {
    return (
      <div className="flex min-h-0 flex-1 flex-col px-8 pb-8 pt-5">
        <EmptyState
          eyebrow="403"
          title="Pouze pro správce"
          description="Nastavení nabídek mění správci firmy a administrátoři."
        />
      </div>
    );
  }

  const parsed = Number(dni);
  const invalid = !/^\d+$/.test(dni.trim()) || parsed < MIN_DNI || parsed > MAX_DNI;
  const dirty = query.data != null && !invalid && parsed !== query.data.platnost_dni;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (invalid) return;
    try {
      await save.mutateAsync({ platnost_dni: parsed });
      toast.ok('Nastavení uloženo');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Uložení se nezdařilo');
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const previewUntil = invalid
    ? null
    : new Date(Date.parse(today) + parsed * 24 * 60 * 60 * 1000).toISOString();

  return (
    <div
      data-testid="nabidky-workspace"
      className="flex min-h-0 flex-1 flex-col gap-5 overflow-auto px-8 pb-8 pt-5"
    >
      {query.isLoading ? (
        <Skeleton className="h-40 w-full max-w-3xl" />
      ) : (
        <div className="grid max-w-5xl gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <Fields cols={1}>
              <Field
                label="Platnost nabídky (dny)"
                required
                state={invalid ? 'error' : 'default'}
                hint={
                  invalid
                    ? `Zadejte celé číslo od ${MIN_DNI} do ${MAX_DNI}.`
                    : 'Platí pro cenové nabídky nových i ojetých vozidel. Výchozí hodnota je 7 dnů.'
                }
                hintTone={invalid ? 'error' : 'neutral'}
              >
                <TextInput
                  data-testid="nabidky-platnost-dni"
                  type="number"
                  inputMode="numeric"
                  min={MIN_DNI}
                  max={MAX_DNI}
                  value={dni}
                  onChange={(e) => setDni(e.currentTarget.value)}
                />
              </Field>
            </Fields>
            <div className="flex justify-end gap-2">
              <Button
                type="submit"
                variant="ink"
                data-testid="nabidky-save"
                disabled={!dirty || save.isPending}
              >
                {save.isPending ? 'Ukládám…' : 'Uložit'}
              </Button>
            </div>
          </form>

          <aside className="flex flex-col gap-2" aria-label="Náhled tisku">
            <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
              Náhled na tisku nabídky
            </span>
            <div
              data-testid="nabidky-preview"
              className="border border-line bg-paper p-5 text-[13px] text-ink-2"
            >
              <div className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                Platnost nabídky
              </div>
              <div className="mt-1">
                <b>{invalid ? '…' : formatDays(parsed)}</b>
                {previewUntil && (
                  <>
                    {' '}
                    · do <b>{formatDate(previewUntil)}</b>
                  </>
                )}
              </div>
              <p className="mt-3 text-[12px] text-ink-3">
                Počítá se od data vytištění nabídky (u ojetých od data nabídky), tady pro dnešek.
              </p>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
